import type { CveDataDisputeReasonsType } from '../cveDisputeTypes';
import type { JiraComponentExport, JiraCustomFieldExport } from '../jira';
import type { LicenseDisputeReasonsType } from '../model-typings/licensedisputes';
import type { MalwareDisputeReasonsType } from '../model-typings/malwaredisputes';
import type { QualityDisputeReasonsType } from '../model-typings/qualitydisputes';
import type { StripePriceLimit } from '../model-typings/stripepricelimits';
import type { FossaPlanName } from '../subscriptions/consts';

import type { Category, QualityIssueType, Scope, Status, VulnIgnoreReason } from './common';
import type { IssueExceptionsFilter } from './exceptions';
import type { LicensingFilter, QualityFilter, VulnFilter } from './filters';
import type {
  CreatedAtSort,
  EpssSort,
  IssueCountSort,
  IssueExceptionOrderBy,
  IssueExceptionSort,
  IssueTypeSort,
  PackageSort,
  SeveritySort,
} from './sort';

export type DataValue = number | undefined | null | string;
export type Operator = '===';
export type VarAssignment = { var: string };
export type Condition = Record<Operator, [VarAssignment, DataValue]>

export type RootConditionOperand = 'and' | 'or';
export type RootCondition = Partial<Record<RootConditionOperand, Condition[]>>;

export type StatusFilter = Status | 'all';

export type CommonIssuesFilterQuery<Filter> = {
  status: StatusFilter;
  scope: Scope;
  filter?: Filter;
  ids?: number[];
  teamId?: number[] | null;
  /**
   * The billing plan to use for the request.
   * Useful for restricting the available filters.
   * If not provided, filters won't be limited.
   */
  billingPlan?: FossaPlanName | null;
  /**
   * The Stripe Limits to use for the request.
   * Useful for restricting the available filters for dynamic plans.
   */
  stripePriceLimit?: StripePriceLimit | null;
  /**
   * Include origin paths for the direct dependency responsible for
   * including the revision(s) affected by this issue.
   */
  includeDirectDependencyOriginPaths?: boolean;
};

type CommonIssuesQuery<Filter, Sort = never> = CommonIssuesFilterQuery<Filter> & {
  sort: PackageSort | CreatedAtSort | IssueCountSort | IssueTypeSort | Sort;
  page: number;
  count: number;
};

export type LicensingIssuesFilterQuery = CommonIssuesFilterQuery<LicensingFilter>;
export type QualityIssuesFilterQuery = CommonIssuesFilterQuery<QualityFilter>;
export type VulnIssuesFilterQuery = CommonIssuesFilterQuery<VulnFilter>;

export type IssuesFilterQuery<C extends Category> =
  C extends Category.Licensing ? LicensingIssuesFilterQuery :
  C extends Category.Vulnerability ? VulnIssuesFilterQuery :
  QualityIssuesFilterQuery;

export type CategoriedIssuesFilterQuery<C extends Category> = IssuesFilterQuery<C> & { category: C };

// These are the request types for GET /api/v2/issues/[type]
export type LicensingIssuesQuery = CommonIssuesQuery<LicensingFilter>;
export type QualityIssuesQuery = CommonIssuesQuery<QualityFilter>;
export type VulnIssuesQuery = CommonIssuesQuery<VulnFilter, SeveritySort | EpssSort>;

export type IssuesQuery<C extends Category> =
  C extends Category.Licensing ? LicensingIssuesQuery :
  C extends Category.Vulnerability ? VulnIssuesQuery :
  QualityIssuesQuery;

export type CategoriedIssuesQuery<C extends Category> = IssuesQuery<C> & { category: C };

export type IssuesByIdQuery = {
  scope: Scope;
  ids: number[];

  /**
   * Even though this is typically a filter, we need to include this to support finding `all` issues when providing a
   * by-id set of filters. This is primarily used for the Issue Overview CSV export.
   */
  status?: StatusFilter;

  /**
   * Include origin paths for the direct dependency responsible for
   * including the revision(s) affected by this issue.
   */
  includeDirectDependencyOriginPaths?: boolean;
};

export type CategoriedIssuesByIdQuery<C extends Category> = IssuesByIdQuery & { category: C };

export type APIIssuesQuery<C extends Category> =
  | CategoriedIssuesQuery<C>
  | CategoriedIssuesByIdQuery<C>;

export type APIIssueSummaryQuery<C extends Category> = Omit<CategoriedIssuesFilterQuery<C>, 'status'>;

export type JiraUniqueTicketInfo = {
  title: string;
  content: string;
  issueId: number;
}

export type JiraIssueExportRequest = {
  type: 'jira';
  title: string;
  description: string;
  uniqueTickets?: JiraUniqueTicketInfo[];
  trackerId?: string;
  jiraId?: string;
  issueType?: string;
  labels?: string[];
  components?: JiraComponentExport[];
  customFields?: JiraCustomFieldExport[];
};

export type CustomIssueExportRequest = {
  type: 'custom';
  url: string;
  comment?: string;
}

export type AnyExportRequest = JiraIssueExportRequest | CustomIssueExportRequest;

// actions
export type ExportAction = { type: 'export'; ticket: JiraIssueExportRequest | CustomIssueExportRequest };

type IgnoreReasonAndNotes = {
  notes?: string;
  reason?: VulnIgnoreReason | null;
}

export type IgnoreAction = { type: 'ignore', } & IgnoreReasonAndNotes;
export type IgnoreActionWithResolutionReasonId = { resolutionReasonId?: number, } & IgnoreAction;

export type UnlinkAction = { type: 'unlink' };
export type UnignoreAction = { type: 'unignore' };

export enum ExceptionPackageScope {
  SelectedVersion = 'SELECTED_VERSION',
  AllVersions = 'ALL_VERSIONS',
}

/**
 * The scope for what the exception covers.
 *
 * Organization - Ignore for the entire org.
 *
 * Policy - Ignore for a specific policy
 *
 * Project - Ignore for only the affected project.
 *
 * ReleaseGroup - Ignore for only the affected release group.
 *
 * ProjectAndReleaseGroups - Ignore for a specific project and all release groups that include it.
 */
export enum ExceptionIgnoreScope {
  Organization = 'ORGANIZATION',
  Policy = 'POLICY',
  Project = 'PROJECT',
  ReleaseGroup = 'RELEASE_GROUP',
  ProjectAndReleaseGroups = 'PROJECT_AND_RELEASE_GROUPS',
}

export type ExceptionAction = {
  type: 'issueException';
  packageScope: ExceptionPackageScope;
  ignoreScope: ExceptionIgnoreScope;
  /**
   * The date after which the exception will expire in YYYY-MM-DD format.
   * Optional so that we can preserve backwards compatibility with current endpoint behavior/requirements.
   */
  expiresAfter?: string | null;
  packageLabelIds?: number[];
  licenseId?: string;
  qualityIssueType?: QualityIssueType,
} & IgnoreReasonAndNotes;

export type IssueExceptionsForDashboard = {
  id: number;
  ignoreScope: ExceptionIgnoreScope;
  packageScope: ExceptionPackageScope;
  issueCategory: Category;
  exceptionUserProjectId: string | null;
  exceptionUserReleaseGroupId: number | null;
  exceptionUserPolicyId: number| null;
  projectTitle: string | null;
  dependencyProjectLocator: string | null;
  dependencyRevisionLocator: string | null;
  dependencyTitle: string | null;
  condition?: RootCondition;
  exceptionTitle: string | null;
  note: string | null;
  createdBy: string;
  reason: string | null;
  policyTitle: string | null;
  packageLabel: string | null;
  expiresAfter: string | null;
  isExpired: boolean;
  createdAt: string;
  updatedAt: string;
  lastTriggeredAt: string | null;
}

export type IssueExceptionsForDashboardResponse = {
  exceptions: IssueExceptionsForDashboard[];
  total: number;
}

export type Action =
  IgnoreAction
  | ExportAction
  | UnlinkAction
  | UnignoreAction
  | ExceptionAction;

// Action Queries are basically a set of filters (revisions, scope, status) but
// can optionally be scoped to specific IDs.
export type APIIssuesActionQuery<C extends Category> =
  CategoriedIssuesFilterQuery<C> & { ids?: number[] };

export const COUNT_DEFAULT = 20;
// COUNT has a different maximum on backend and frontend because the frontend should only
// allow the rendering of COUNT_MAX_FE issues at a time, but the backend should be able
// to respond with more issues if requested.
export const COUNT_MAX_FE = 50;
export const COUNT_MAX_BE = 1000;
export const COUNT_MIN = 5;
export const PAGE_DEFAULT = 1;
export const PAGE_MAX = Infinity;
export const PAGE_MIN = 1;

// The JIRA API v2 supports creating up to 50 tickets at once via the bulk endpoint:
// https://developer.atlassian.com/cloud/jira/platform/rest/v2/api-group-issues/#api-rest-api-2-issue-bulk-post
export const JIRA_BULK_CREATE_ISSUE_MAX = 50;

// JIRA descriptions have an internal limit of 32,767 characters:
// https://jira.atlassian.com/browse/JRACLOUD-68949
export const JIRA_CONTENT_MAX_LENGTH = 32767;

export type VulnDisputeRequest = {
  comment: string;
  reason: CveDataDisputeReasonsType;
}

export type LicenseDisputeRequest = {
  comment: string;
  reason: LicenseDisputeReasonsType;
}

export type LicenseDisputeResponse = {
  id: number,
  comment: string,
  createdBy: number,
  createdAt: string,
  disputedIssueId: number,
  reason: LicenseDisputeReasonsType,
  licenseId: string,
  resolvedAt: string | null,
  resolvedBy: number | null,
}

export type MalwareDisputeRequest = {
  comment?: string | null;
  reason: MalwareDisputeReasonsType;
};

export type MalwareDisputeResponse = {
  id: number;
  comment: string | null;
  createdBy: number;
  createdAt: string;
  disputedIssueId: number;
  reason: MalwareDisputeReasonsType;
  resolvedAt: string | null;
  resolvedBy: number | null;
};

export type QualityDisputeRequest = {
  comment?: string | null;
  reason: QualityDisputeReasonsType;
};

export type QualityDisputeResponse = {
  id: number;
  comment: string | null;
  createdBy: number;
  createdAt: string;
  disputedIssueId: number;
  reason: QualityDisputeReasonsType;
  resolvedAt: string | null;
  resolvedBy: number | null;
};

export type GetIssueExceptionsRequestQuery = {
  filters: IssueExceptionsFilter;
  search?: string;
  page?: number;
  count?: number;
  sortBy?: IssueExceptionSort;
  orderBy?: IssueExceptionOrderBy;
  csv?: boolean;
}

export type GetIssueExceptionsResponse = {
  exceptions: IssueExceptionsForDashboard[];
  total: number;
}

export type GetIssueExceptionParams = {
  id: string;
}

export type GetIssueExceptionResponse = IssueExceptionsForDashboard;

export type GetLicenseIssuesLicensesListRequest = {
  category: Category.Licensing;
  scope: Scope;
  teamId?: number | number[] | 'null';
}

export type GetIssueMalwareParams = {
  id: number;
}
